import React from 'react'
import _ from 'lodash/fp'
import { connect } from 'react-redux'
import moment from 'moment'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { countries } from 'countries-list'

// Config
import { materialGreens } from '@fe/ui/colors'

// Reducers
import * as ClubsReducer from '../../reducers/clubs'

// Material UI things
import withStyles from '@material-ui/core/styles/withStyles'
import FormHelperText from '@material-ui/core/FormHelperText'
import InputAdornment from '@material-ui/core/InputAdornment'
import InputLabel from '@material-ui/core/InputLabel'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'

const TeeLengthInput = props => {
  const initVal = _.get('profile.teeLength', props)
  const [val, setVal] = React.useState('')
  React.useEffect(() => {
    // Wait for profile to be loaded
    if (initVal && !val) {
      setVal(initVal)
    }
  }, [val, initVal])

  const handleChange = event => {
    const text = _.get('target.value', event).replace(/[^\d]/g, '')
    setVal(text)
    const num = Number(text)
    props.onChange(Math.trunc(num * Math.pow(10, 4 - _.size(String(num)))))
  }
  return (
    <TextField
      fullWidth
      InputProps={{
        endAdornment: <InputAdornment position='end'>m</InputAdornment>
      }}
      helperText='Your favorite tee distance is the total 18 hole distance in meters of the tee you usually play. Add it, so GLFR can select the tee that best fits your game.'
      label='FAVORITE TEE LENGTH'
      value={val}
      type='number'
      onChange={handleChange}
      onBlur={() => setVal(initVal)}
    />
  )
}

const styles = theme => ({
  textField: {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  buttonProgress: {
    color: materialGreens[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  }
})

const countriesWithKeys = _.flow(
  _.mapValues.convert({ cap: false })((v, k) => {
    return _.set('value', _.lowerCase(k), v)
  })
)(countries)

const labelify = (string) => {
  return _.flow(_.upperCase)(string)
}

const sexes = {
  male: { value: 'male', label: 'Male' },
  female: { value: 'female', label: 'Female' }
}

function ProfileForm ({
  profile,
  setProfile,
  showPrivacySettings = false,
  // redux
  clubs,
  getClubs,
  // styles
  classes
}) {
  const currentlySelectedCountry = React.useMemo(() =>
    _.get(_.upperCase(_.get('countryCode', profile)), countriesWithKeys),
  [profile])

  // getClubs changes all the time - just freeze it in time
  const getClubsProxy = React.useCallback(getClubs, [])

  React.useEffect(() => {
    getClubsProxy()
  }, [getClubsProxy])

  const fieldUpdater = React.useCallback(field => event => {
    if (_.get('target', event)) {
      const value = event.target.value
      setProfile(_.set(field, value))
    } else {
      setProfile(_.set(field, event))
    }
  }, [setProfile])

  const textFieldFromField = React.useCallback((field, type = 'text') => {
    return (
      <TextField
        fullWidth
        type={type}
        label={labelify(field)}
        value={_.get(field, profile)}
        className={classes.textField}
        onChange={fieldUpdater(field)}
      />
    )
  }, [profile, fieldUpdater, classes])

  return (
    <>
      <Typography style={{ width: '100%', marginTop: 24 }} align='left' variant='h4'>Personal</Typography>

      {textFieldFromField('name')}

      <InputLabel style={{ alignSelf: 'flex-start' }} shrink>{labelify('birthday')}</InputLabel>
      <TextField
        fullWidth
        className={classes.textField}
        type='date'
        value={moment.utc(_.get('birthday', profile)).format('YYYY-MM-DD')}
        onChange={(event) => {
          fieldUpdater('birthday')(moment.utc(event.target.value).valueOf())
        }}
      />

      <InputLabel style={{ alignSelf: 'flex-start' }} shrink>{labelify('sex')}</InputLabel>
      <Select
        onChange={(option) => fieldUpdater('sex')(_.get('value', option))}
        className={classes.textField}
        value={_.get(_.get('sex', profile), sexes)}
        options={_.values(sexes)}
      />

      {textFieldFromField('email')}

      <InputLabel style={{ alignSelf: 'flex-start' }} shrink>{labelify('country')}</InputLabel>
      <Select
        onChange={(option) => fieldUpdater('countryCode')(_.get('value', option))}
        className={classes.textField}
        value={currentlySelectedCountry}
        options={_.values(countriesWithKeys)}
        getOptionLabel={_.get('name')}
      />

      <Typography style={{ width: '100%', marginTop: 24 }} align='left' variant='h4'>Golfer</Typography>

      <InputLabel style={{ alignSelf: 'flex-start' }} shrink>{labelify('home club')}</InputLabel>
      <CreatableSelect
        onChange={option => {
          fieldUpdater('clubName')(_.get('name', option))
          fieldUpdater('clubId')(_.get('id', option))
        }}
        className={classes.textField}
        value={{ name: _.get('clubName', profile) }}
        options={clubs}
        getOptionLabel={_.get('name')}
        getOptionValue={_.get('name')}
        getNewOptionData={(inputValue, optionLabel) => ({
          name: inputValue,
          __isNew__: true
        })}
      />

      {textFieldFromField('handicap', 'number')}

      <TeeLengthInput profile={profile} onChange={fieldUpdater('teeLength')} />

      {showPrivacySettings && (
        <PrivacySettings
          profile={profile}
          fieldUpdater={fieldUpdater}
          classes={classes}
        />
      )}
    </>
  )
}

const visibilities = {
  friends: { value: 'friends', label: 'Buddies' },
  public: { value: 'public', label: 'Public' },
  private: { value: 'private', label: 'Private' }
}

function PrivacySettings ({
  profile,
  fieldUpdater,
  classes
}) {
  return (
    <>
      <Typography style={{ width: '100%', marginTop: 24 }} align='left' variant='h4'>Settings</Typography>

      <InputLabel style={{ alignSelf: 'flex-start' }} shrink>{labelify('Game Privacy')}</InputLabel>
      <FormHelperText>
        If set to Public, you can share your games with friends and family even when they do not have a user.
        When set to Buddies only your buddies who are logged in can see your games.
        If set to Private, only you can see your games.
      </FormHelperText>
      <Select
        onChange={(option) => fieldUpdater('visibility')(_.get('value', option))}
        className={classes.textField}
        value={_.get(_.get('visibility', profile), visibilities) || undefined}
        options={_.values(visibilities)}
      />
    </>
  )
}

export default _.compose(
  withStyles(styles),
  connect(
    (s, _ownProps) => {
      const clubs = ClubsReducer.selectors.clubs(s)
      return {
        clubs: _.flow(_.values, _.sortBy('name'))(clubs)
      }
    },
    (d, _ownProps) => {
      return {
        getClubs: () => d(ClubsReducer.actions.clubsRequest())
      }
    }
  )
)(ProfileForm)
